import { useCallback, useEffect, useRef, useState } from "react";
import { api, put } from "./api";
import "./P2PTemplateEditor.css";

type Template = { event_type: string; label: string; body: string; enabled: boolean; version: number; default_body: string };
type Variable = { key: string; label: string; example: string };
type Catalogue = { items: Template[]; variables: Variable[] };
type Draft = { body: string; enabled: boolean };
type Preview = { key: string; text?: string; length?: number; error?: string };
const endpoint = "/api/v1/p2p/notifications/templates";
const message = (error: unknown) => error instanceof Error ? error.message : String(error);

export default function P2PTemplateEditor({ onDirtyChange }: { onDirtyChange?: (dirty: boolean) => void }) {
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [eventType, setEventType] = useState("ORDER_CREATED");
  const [exchange, setExchange] = useState("BYBIT");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const loadSequence = useRef(0);
  const selected = catalogue?.items.find(item => item.event_type === eventType);
  const draft = drafts[eventType];
  const body = draft?.body ?? "";
  const changed = (item: Template) => drafts[item.event_type]?.body !== item.body || drafts[item.event_type]?.enabled !== item.enabled;
  const dirty = catalogue?.items.some(changed) ?? false;
  const selectedDirty = selected ? changed(selected) : false;
  const previewKey = JSON.stringify([eventType, exchange, body]);
  const currentPreview = preview?.key === previewKey ? preview : null;

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setError(""); setNotice("");
    try {
      const data = await api<Catalogue>(endpoint, { cache: "no-store" });
      if (sequence !== loadSequence.current) return;
      setCatalogue(data);
      setDrafts(Object.fromEntries(data.items.map(item => [item.event_type, { body: item.body, enabled: item.enabled }])));
    } catch (reason) { if (sequence === loadSequence.current) setError(message(reason)); }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, []);

  const cancelLoad = useCallback(() => { loadSequence.current++; }, []);
  useEffect(() => { void load(); return cancelLoad; }, [load, cancelLoad]);
  useEffect(() => {
    onDirtyChange?.(dirty);
    const leave = (event: BeforeUnloadEvent) => { if (dirty) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", leave);
    return () => { window.removeEventListener("beforeunload", leave); onDirtyChange?.(false); };
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!catalogue) return;
    let active = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      api<{ text: string; length: number }>(`${endpoint}/preview`, {
        method: "POST", signal: controller.signal,
        body: JSON.stringify({ event_type: eventType, exchange, body }),
      }).then(result => {
        if (active) setPreview({ key: previewKey, ...result });
      }).catch(reason => {
        if (active) setPreview({ key: previewKey, error: message(reason) });
      });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); controller.abort(); };
  }, [body, eventType, exchange, previewKey, catalogue]);

  const edit = (patch: Partial<Draft>) => {
    setDrafts(previous => ({ ...previous, [eventType]: { ...previous[eventType], ...patch } }));
    setNotice(""); setError("");
  };
  const insert = (key: string) => {
    const input = textarea.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const token = `{{${key}}}`;
    edit({ body: body.slice(0, start) + token + body.slice(end) });
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(start + token.length, start + token.length); });
  };
  const save = async () => {
    if (!selected || !draft || saving) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const result = await put<Omit<Template, "label" | "default_body">>(`${endpoint}/${eventType}`, { ...draft, version: selected.version });
      setCatalogue(previous => previous && ({ ...previous, items: previous.items.map(item => item.event_type === result.event_type ? { ...item, ...result } : item) }));
      setNotice(result.enabled
        ? "Сохранено. Этот шаблон применяется при следующих отправках во все настроенные каналы."
        : "Сохранено. Для этого события используется стандартный формат.");
    } catch (reason) { setError(message(reason)); }
    finally { setSaving(false); }
  };

  return <section className="tg-templates" aria-label="Редактор шаблонов Telegram">
    <div className="tg-template-heading">
      <div><h2>Шаблоны Telegram</h2><p>Вставьте подготовленный текст и замените данные переменными. Переносы строк и обычные эмодзи сохраняются.</p></div>
      <button disabled={loading || saving} onClick={() => {
        if (!dirty || window.confirm("Загрузить сохранённые шаблоны и отменить все несохранённые правки?")) void load();
      }}>Загрузить сохранённые</button>
    </div>
    {error && <div className="alert error" role="alert">{error}</div>}
    {notice && <div className="alert info" role="status">{notice}</div>}
    {!catalogue && <p role="status">{loading ? "Загружаем шаблоны…" : "Шаблоны не загружены. Повторите загрузку."}</p>}
    {catalogue && selected && draft && <>
      <div className="tg-template-events" role="group" aria-label="Событие уведомления">
        {catalogue.items.map(item => <button key={item.event_type} aria-pressed={eventType === item.event_type} disabled={saving || loading}
          onClick={() => { setEventType(item.event_type); setError(""); setNotice(""); }}>
          <span>{item.label}</span><small>{changed(item) ? "Есть правки" : item.enabled ? "Свой шаблон" : "Стандартный"}</small>
        </button>)}
      </div>
      <div className="tg-template-grid">
        <div className="tg-template-compose panel">
          <div className="tg-template-title"><h3>Текст уведомления</h3><span>{selectedDirty ? "Не сохранено" : `Версия ${selected.version}`}</span></div>
          <label className="check"><input type="checkbox" checked={draft.enabled} disabled={saving || loading} onChange={event => edit({ enabled: event.target.checked })} />Использовать свой шаблон для этого события</label>
          <label className="field" htmlFor="tg-template-body"><span>Сообщение</span></label>
          <textarea id="tg-template-body" ref={textarea} value={body} spellCheck={false} disabled={saving || loading}
            aria-describedby="tg-template-help" aria-invalid={Boolean(currentPreview?.error)}
            onChange={event => edit({ body: event.target.value })} />
          <div className="tg-template-tools">
            <button disabled={saving || loading} onClick={() => edit({ body: selected.default_body })}>Вставить пример</button>
            <button disabled={!selectedDirty || saving || loading} onClick={() => edit({ body: selected.body, enabled: selected.enabled })}>Отменить правки</button>
            <span>{body.length} / 4096</span>
          </div>
          <p id="tg-template-help" className="form-note">Обычный текст, без HTML и Markdown. Нажмите на переменную ниже, чтобы вставить её на место курсора. Если данных нет, придёт «-».</p>
          <div className="tg-template-save">
            <button className="primary" disabled={!selectedDirty || saving || loading || !currentPreview?.text || Boolean(currentPreview.error)} onClick={() => void save()}>
              {saving ? "Сохраняем…" : "Сохранить шаблон"}
            </button>
            <span>{draft.enabled ? "Для всех каналов этого события" : "Стандартный формат останется активным"}</span>
          </div>
          <details className="tg-template-variables">
            <summary>Переменные</summary>
            <div>{catalogue.variables.map(variable => <button key={variable.key} disabled={saving || loading}
              title={`${variable.label}. Пример: ${variable.example}`} onClick={() => insert(variable.key)}>
              <span>{variable.label}</span><code>{`{{${variable.key}}}`}</code>
            </button>)}</div>
          </details>
        </div>
        <aside className="tg-template-preview panel">
          <div className="tg-template-title"><h3>Предпросмотр</h3><span>Пример данных</span></div>
          <label className="field"><span>Биржа в примере</span><select value={exchange} onChange={event => setExchange(event.target.value)}>
            <option>BYBIT</option><option>HTX</option><option>MEXC</option>
          </select></label>
          <div className="tg-template-chat" aria-live="polite" aria-busy={!currentPreview}>
            {currentPreview?.error ? <div className="alert error" role="alert">{currentPreview.error}</div>
              : currentPreview?.text ? <div className="tg-template-bubble"><div className="tg-template-sender">Ордер инфо | GShard</div><div className="tg-template-message">{currentPreview.text}</div><div className="tg-template-time">пример · 17:00</div></div>
                : <p className="form-note">Проверяем шаблон…</p>}
          </div>
          <p className="form-note">{currentPreview?.length !== undefined && `${currentPreview.length} / 4096 после подстановки. `}Предпросмотр показывает редактируемый текст. Сообщения в Telegram не отправляются.</p>
          <p className="form-note">Каждое событие приходит отдельным сообщением. Изменения не редактируют ранее отправленные уведомления.</p>
        </aside>
      </div>
    </>}
  </section>;
}
